/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

import React from "react";
import axios from "axios";
import { Auth } from "aws-amplify";
import { eraseAllCookies } from "../../hooks/cookieHelper";
import { cleanLocalStorage } from "../../hooks/tokenHelper";
import LoadingPage from "../LandingPage/loadingPage";
import { QUERY_PARAMS_KEYS, STORAGE_KEYS } from "../../const";

class Logout extends React.Component {
	componentDidMount() {
		var redirectInfo = localStorage.getItem(STORAGE_KEYS.redirectInfo);

		if (redirectInfo) {
			// For handling a redirect back from the Cognito Hosted UI
			localStorage.removeItem(STORAGE_KEYS.redirectInfo);
			var rdJSON;
			try {
				rdJSON = JSON.parse(redirectInfo);
			} catch (e) {
				// Corrupted / non-JSON redirectInfo would otherwise throw during
				// componentDidMount and leave the user stuck on the "Signing out" screen.
				console.warn("[Logout] redirectInfo was not valid JSON → /");
				window.location.replace("/");
				return;
			}
			if (
				rdJSON["resumeAuth"] &&
				rdJSON["clientID"] &&
				rdJSON["redirectURI"] &&
				rdJSON["authCode"]
			) {
				// Resume a PKCE login that was interrupted by a forced sign-out, instead of
				// dead-ending at /app-logout. The original authorization_code is reused (it
				// was persisted by the Authorize lambda) so the flow completes after re-login.
				var resumeParams = new URLSearchParams();
				resumeParams.set(QUERY_PARAMS_KEYS.clientID, rdJSON["clientID"]);
				resumeParams.set(
					QUERY_PARAMS_KEYS.redirectURI,
					rdJSON["redirectURI"]
				);
				resumeParams.set(QUERY_PARAMS_KEYS.authCode, rdJSON["authCode"]);
				if (rdJSON["state"]) {
					resumeParams.set(QUERY_PARAMS_KEYS.state, rdJSON["state"]);
				}
				window.location.replace("/?" + resumeParams.toString());
			} else if (rdJSON["clientID"] && rdJSON["logoutURI"] && rdJSON["logoutURI"] != "null") {
				// Redirect back to client
				axios
					.get("/verifyClient", {
						params: {
							client_id: rdJSON["clientID"],
							logout_uri: rdJSON["logoutURI"],
						},
					})
					.then((response) => {
						if (response.status === 200) {
							window.location.assign(rdJSON["logoutURI"]);
						}
					})
					.catch((error) => {
						console.error(
							"[Logout] /verifyClient failed → /app-logout " +
								JSON.stringify({
									status:
										error &&
										error.response &&
										error.response.status,
									data:
										error &&
										error.response &&
										error.response.data,
								})
						);
						// Default to redirecting to the broker logout page
						window.location.href = "/app-logout";
					});
			} else if (
				rdJSON["responseType"] === "id_token" &&
				rdJSON["clientID"] &&
				rdJSON["redirectURI"]
			) {
				// Call authorize endpoint to start implicit flow
				let authorizeEndpointPath =
					"/oauth2/authorize/?response_type=" +
					rdJSON["responseType"] +
					"&client_id=" +
					rdJSON["clientID"] +
					"&redirect_uri=" +
					rdJSON["redirectURI"];
				window.location.href = authorizeEndpointPath;
			} else if (
				rdJSON["responseType"] &&
				rdJSON["clientID"] &&
				rdJSON["redirectURI"] &&
				rdJSON["codeChallenge"] &&
				rdJSON["codeChallengeMethod"]
			) {
				// Call authorize endpoint to start PKCE flow
				let authorizeEndpointPath =
					"/oauth2/authorize/?response_type=" +
					rdJSON["responseType"] +
					"&client_id=" +
					rdJSON["clientID"] +
					"&redirect_uri=" +
					rdJSON["redirectURI"] +
					"&code_challenge=" +
					rdJSON["codeChallenge"] +
					"&code_challenge_method=" +
					rdJSON["codeChallengeMethod"] +
					"&state=" +
					rdJSON["state"];
				window.location.href = authorizeEndpointPath;
			} else {
				// No known redirect shape — send a plain sign-out to the login page rather
				// than /app-logout, whose "registration cancelled" message is reserved for
				// the actual cancel-registration flow.
				// Stringify at log time: the redirect below makes a live object reference
				// read as stale ("No properties") once DevTools lazily expands it.
				console.warn(
					"[Logout] redirectInfo matched no known shape → / " +
						JSON.stringify(rdJSON)
				);
				window.location.replace("/");
			}
		} else {
			// If the logout endpoint is being called before the user has been logged out
			// Erase the token cookies
			eraseAllCookies();

			// Accept requests according to https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
			let queryStringParams = new URLSearchParams(window.location.search);
			let clientID = queryStringParams.get(QUERY_PARAMS_KEYS.clientID);
			let logoutURI = queryStringParams.get(QUERY_PARAMS_KEYS.logoutURI);
			let redirectURI = queryStringParams.get(
				QUERY_PARAMS_KEYS.redirectURI
			);
			let responseType = queryStringParams.get(
				QUERY_PARAMS_KEYS.responseType
			);
			let codeChallenge = queryStringParams.get(
				QUERY_PARAMS_KEYS.challenge
			);
			let codeChallengeMethod = queryStringParams.get(
				QUERY_PARAMS_KEYS.challengeMethod
			);
			let state = queryStringParams.get(
				QUERY_PARAMS_KEYS.state
			);

			// Store the redirect info in local storage before calling the Cognito Hosted UI to logout
			var redirectObject;
			if (clientID && logoutURI) {
				// For redirect to client
				redirectObject = { clientID: clientID, logoutURI: logoutURI };
			} else if (responseType === "id_token" && clientID && redirectURI) {
				// For implicit flow
				redirectObject = {
					clientID: clientID,
					responseType: responseType,
					redirectURI: redirectURI,
				};
			} else if (
				responseType === "code" &&
				clientID &&
				redirectURI &&
				codeChallenge &&
				codeChallengeMethod
			) {
				// For PKCE flow
				redirectObject = {
					clientID: clientID,
					responseType: responseType,
					redirectURI: redirectURI,
					codeChallenge: codeChallenge,
					codeChallengeMethod: codeChallengeMethod,
					state: state
				};
			} else {
				redirectObject = { redirect: "broker" };
			}
			localStorage.setItem(
				STORAGE_KEYS.redirectInfo,
				JSON.stringify(redirectObject)
			);

			// We remove the client-id used for last login
			cleanLocalStorage();

			// Sign the user out
			Auth.signOut().then(() => {
				window.location.reload(); // Reload the page to handle the client redirect
			});
		}
	}

	render() {
		return <LoadingPage text="Signing out" />;
	}
}

export default Logout;
